package com.schoolconnect.app.feature.schools

import com.schoolconnect.app.datasource.network.response.SchoolResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.request.SchoolCreateRequest
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.AdminPagedListCoordinator
import com.schoolconnect.app.feature.common.resolveServerPageTotal
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.CancellationException

class SchoolsViewModel(
    private val shellRoleCode: String = ""
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] SchoolsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(SchoolsUiState())
    private var loadJob: Job? = null
    private var analyticsJob: Job? = null
    private val searchDebouncer = DebouncedActionRunner(scope)
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private val pagedListCoordinator = AdminPagedListCoordinator(searchDebouncer, bootstrapSequencer)
    private val isSuperAdmin = shellRoleCode.trim() == SchoolKeys.V_ROLE_APP_SUPER_ADMIN

    val uiState: StateFlow<SchoolsUiState> = _uiState.asStateFlow()

    init {
        refreshSchoolsData(forceRemote = false)
    }

    fun onEvent(event: SchoolsUiEvent) {
        when (event) {
            SchoolsUiEvent.BackClicked -> Unit
            SchoolsUiEvent.RefreshRequested -> {
                refreshSchoolsData(forceRemote = true)
            }
            SchoolsUiEvent.AddSchoolClicked -> setScreenMode(SchoolsScreenMode.ADD, clearSelection = true)
            is SchoolsUiEvent.SearchChanged -> {
                _uiState.update {
                    it.copy(
                        searchQuery = event.query,
                        paging = it.paging.copy(pageIndex = 0),
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                pagedListCoordinator.schedulePage { forceRemote -> refreshSchools(forceRemote) }
            }
            is SchoolsUiEvent.FilterChanged -> {
                _uiState.update {
                    it.copy(
                        filters = event.filter,
                        paging = it.paging.copy(pageIndex = 0),
                        errorMessage = "",
                        infoMessage = ""
                    )
                }
                pagedListCoordinator.requestPage(forceRemote = false) { forceRemote -> refreshSchools(forceRemote) }
            }
            is SchoolsUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.isSortAscending else true
                    it.copy(
                        sortField = event.field,
                        isSortAscending = nextAscending,
                        paging = it.paging.copy(pageIndex = 0)
                    )
                }
                pagedListCoordinator.requestPage(forceRemote = true) { forceRemote -> refreshSchools(forceRemote, "") }
            }
            is SchoolsUiEvent.PageChanged -> {
                _uiState.update {
                    it.copy(paging = it.paging.copy(pageIndex = event.pageIndex.coerceAtLeast(0)))
                }
                pagedListCoordinator.requestPage(forceRemote = false) { forceRemote -> refreshSchools(forceRemote) }
            }
            is SchoolsUiEvent.PageSizeChanged -> {
                _uiState.update {
                    it.copy(paging = it.paging.copy(pageSize = event.pageSize.coerceAtLeast(1), pageIndex = 0))
                }
                pagedListCoordinator.requestPage(forceRemote = false) { forceRemote -> refreshSchools(forceRemote) }
            }
            is SchoolsUiEvent.SaveSchool -> saveSchool(event.draft)
            is SchoolsUiEvent.EditSchoolClicked -> _uiState.update {
                it.copy(
                    screenMode = SchoolsScreenMode.EDIT,
                    selectedSchoolId = event.school.id,
                    selectedSchool = event.school
                )
            }
            is SchoolsUiEvent.ViewSchoolClicked -> _uiState.update {
                it.copy(
                    screenMode = SchoolsScreenMode.DETAILS,
                    selectedSchoolId = event.school.id,
                    selectedSchool = event.school
                )
            }
            SchoolsUiEvent.CloseDetailView -> setScreenMode(SchoolsScreenMode.LIST)
            SchoolsUiEvent.CloseActiveScreen -> setScreenMode(SchoolsScreenMode.LIST, clearSelection = true)
            is SchoolsUiEvent.ToggleSchoolStatus -> toggleSchoolStatus(event.schoolId)
            is SchoolsUiEvent.DeleteSchool -> deleteSchool(event.schoolId)
            is SchoolsUiEvent.ManageUsers -> _uiState.update {
                val school = it.schools.firstOrNull { school -> school.id == event.schoolId }
                it.copy(infoMessage = school?.let { s -> "Manage users for ${s.name}" } ?: "Manage users")
            }
            is SchoolsUiEvent.ViewDetails -> _uiState.update {
                val school = it.schools.firstOrNull { school -> school.id == event.schoolId }
                it.copy(infoMessage = school?.let { s -> "Viewing details for ${s.name}" } ?: "School details opened")
            }
            SchoolsUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", infoMessage = "")
            }
        }
    }

    private fun setScreenMode(mode: SchoolsScreenMode, clearSelection: Boolean = false) {
        _uiState.update {
            it.copy(
                screenMode = mode,
                selectedSchoolId = when {
                    clearSelection || mode == SchoolsScreenMode.LIST -> ""
                    else -> it.selectedSchoolId
                },
                selectedSchool = if (clearSelection || mode == SchoolsScreenMode.LIST) null else it.selectedSchool
            )
        }
    }

    private fun refreshSchools(forceRemote: Boolean) {
        refreshSchools(forceRemote = forceRemote, successMessage = "")
    }

    private fun refreshSchoolsData(forceRemote: Boolean) {
        pagedListCoordinator.refresh(
            forceRemote = forceRemote,
            loadAnalytics = { if (isSuperAdmin) refreshSchoolsAnalytics(forceRemote) },
            loadPage = { pageForceRemote -> refreshSchools(pageForceRemote) }
        )
    }

    private fun refreshSchools(
        forceRemote: Boolean,
        successMessage: String
    ) {
        val state = _uiState.value
        val query = state.searchQuery.trim()
            val status = when (state.filters.status) {
                SchoolStatusFilter.ALL -> null
                SchoolStatusFilter.ACTIVE -> true
                SchoolStatusFilter.INACTIVE -> false
            }
            val advancedFiltersActive = state.filters.hasAdvancedFilters()
            val requestPageIndex = if (advancedFiltersActive) 0 else state.paging.pageIndex
            val requestPageSize = if (advancedFiltersActive) 100 else state.paging.pageSize

        loadJob?.cancel()
        loadJob = scope.launch {
            scLog(
                "$TAG refreshSchools start q=${query.ifBlank { "<blank>" }} status=${status?.toString() ?: "<any>"} pageIndex=${state.paging.pageIndex} pageSize=${state.paging.pageSize}"
            )
            _uiState.update {
                it.copy(
                    isLoading = true,
                    errorMessage = "",
                    infoMessage = ""
                )
            }

            val result = SchoolRepository.loadSchoolsPage(
                query = query,
                isActive = status,
                pageIndex = requestPageIndex,
                pageSize = requestPageSize,
                sort = ServerSort(
                    field = state.sortField.apiField,
                    order = if (state.isSortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                ),
                withStudentCounts = isSuperAdmin,
                forceRemote = forceRemote
            )

            result.fold(
                onSuccess = { remoteSchools ->
                    val serverTotalCount = remoteSchools.serverTotalCountOrNull()
                    val mappedSchools = remoteSchools.map { it.toUiRow() }
                    val exactTotalCount = resolveServerPageTotal(
                        exactTotal = serverTotalCount,
                        paging = state.paging,
                        currentItemCount = mappedSchools.size
                    )
                    val nextPageCount = pageCountForTotal(
                        totalCount = exactTotalCount.totalCount,
                        pageSize = state.paging.pageSize
                    )
                    _uiState.update { current ->
                        current.copy(
                            isLoading = false,
                            schools = mappedSchools,
                            paging = current.paging.copy(
                                pageIndex = current.paging.pageIndex.coerceAtMost(maxOf(0, nextPageCount - 1)),
                                totalCount = if (advancedFiltersActive) mappedSchools.size else exactTotalCount.totalCount,
                                isTotalCountExact = exactTotalCount.isExact
                            ),
                            errorMessage = "",
                            infoMessage = successMessage
                        )
                    }
                    scLog(
                        "$TAG refreshSchools success count=${mappedSchools.size} totalCount=${exactTotalCount.totalCount} exact=${exactTotalCount.isExact}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshSchools cancelled")
                    } else {
                        scLog("$TAG refreshSchools failure", throwable)
                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                errorMessage = throwable.message ?: "Failed to load schools.",
                                infoMessage = ""
                            )
                        }
                    }
                }
            )
        }
    }

    private fun refreshSchoolsAnalytics(forceRemote: Boolean) {
        if (!isSuperAdmin) return

        analyticsJob?.cancel()
        analyticsJob = scope.launch {
            scLog("$TAG refreshSchoolsAnalytics start forceRemote=$forceRemote role=$shellRoleCode")
            _uiState.update {
                it.copy(isAnalyticsLoading = true)
            }

            val result = SchoolRepository.loadSchoolsAnalytics(forceRemote = forceRemote)
            result.fold(
                onSuccess = { analytics ->
                    _uiState.update {
                        it.copy(
                            isAnalyticsLoading = false,
                            schoolsAnalytics = analytics
                        )
                    }
                    scLog(
                        "$TAG refreshSchoolsAnalytics success total=${analytics.totalSchools} active=${analytics.metrics.activeSchools} inactive=${analytics.metrics.suspendedOrInactiveSchools}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshSchoolsAnalytics cancelled")
                    } else {
                        scLog("$TAG refreshSchoolsAnalytics failure", throwable)
                        _uiState.update {
                            it.copy(
                                isAnalyticsLoading = false
                            )
                        }
                    }
                }
            )
        }
    }

    private fun saveSchool(draft: SchoolEditorDraft) {
        _uiState.update {
            it.copy(
                isSavingSchool = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            val request = runCatching {
                // Submit must use the same refreshed lookup snapshot as the
                // Add School selectors; never convert a display label while
                // the lookup request is still in flight.
                SystemLookupRepository.refresh(forceRemote = false).getOrElse { throw it }
                draft.toSchoolCreateRequest()
            }.getOrElse { throwable ->
                _uiState.update {
                    it.copy(
                        isSavingSchool = false,
                        errorMessage = throwable.message ?: "Unable to load school lookup values. Please retry."
                    )
                }
                return@launch
            }
            val validationError = request.validateCreateSchool()
            if (validationError != null) {
                _uiState.update {
                    it.copy(
                        isSavingSchool = false,
                        errorMessage = validationError
                    )
                }
                return@launch
            }
            val result = SchoolRepository.createSchoolWithLogo(
                school = request,
                logoFileName = draft.schoolLogoFileName,
                logoBytes = draft.schoolLogoBytes
            )
            result.fold(
                onSuccess = { outcome ->
                    val createdSchool = outcome.school
                    val createdRow = createdSchool.toUiRow()
                    val logoUploadFailure = outcome.logoUploadFailure
                    _uiState.update { state ->
                        val updatedSchools = buildList {
                            state.schools.forEach { school ->
                                if (school.id != createdRow.id) add(school)
                            }
                            add(createdRow)
                        }

                        state.copy(
                            isSavingSchool = false,
                            schools = updatedSchools,
                            paging = state.paging.copy(pageIndex = 0),
                            errorMessage = logoUploadFailure?.message.orEmpty(),
                            infoMessage = if (logoUploadFailure == null) {
                                "Added ${createdRow.name}"
                            } else {
                                "Added ${createdRow.name}; logo upload failed. Edit the school to retry."
                            }
                        )
                    }
                    refreshSchoolsData(forceRemote = true)
                    setScreenMode(SchoolsScreenMode.LIST, clearSelection = true)
                },
                onFailure = { throwable ->
                    scLog("$TAG createSchool failure", throwable)
                    _uiState.update {
                        it.copy(
                            isSavingSchool = false,
                            errorMessage = throwable.message ?: "Failed to add school.",
                            infoMessage = ""
                        )
                    }
                }
            )
        }
    }

    private fun toggleSchoolStatus(schoolId: String) {
        val school = _uiState.value.schools.firstOrNull { it.id == schoolId }
        if (school == null) {
            _uiState.update {
                it.copy(errorMessage = "School not found.", infoMessage = "")
            }
            return
        }

        val nextActive = !school.isActive
        _uiState.update { state ->
            state.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            scLog(
                "$TAG toggleSchoolStatus start id=${school.id} code=${school.code} nextActive=$nextActive"
            )
            val result = SchoolRepository.toggleSchoolStatus(school.code, nextActive)
            result.fold(
                onSuccess = {
                    scLog(
                        "$TAG toggleSchoolStatus success id=${school.id} code=${school.code} nextActive=$nextActive"
                    )
                    refreshSchoolsData(forceRemote = true)
                },
                onFailure = { throwable ->
                    scLog("$TAG toggleSchoolStatus failure id=${school.id} code=${school.code}", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to update school status.",
                            infoMessage = ""
                        )
                    }
                }
            )
        }
    }

    private fun deleteSchool(schoolId: String) {
        val school = _uiState.value.schools.firstOrNull { it.id == schoolId }
        if (school == null) {
            _uiState.update {
                it.copy(errorMessage = "School not found.", infoMessage = "")
            }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            scLog("$TAG deleteSchool start id=${school.id} code=${school.code}")
            val result = SchoolRepository.deleteSchool(school.code)
            result.fold(
                onSuccess = {
                    scLog("$TAG deleteSchool success id=${school.id} code=${school.code}")
                    refreshSchoolsData(forceRemote = true)
                    _uiState.update {
                        it.copy(
                            paging = it.paging.copy(pageIndex = 0),
                            infoMessage = "Deleted ${school.name}"
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("$TAG deleteSchool failure id=${school.id} code=${school.code}", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to delete school.",
                            infoMessage = ""
                        )
                    }
                }
            )
        }
    }

    private fun SchoolResponse.toUiRow(): SchoolRowUi {
        val location = buildList {
            addressLine1.orEmpty().takeIf { it.isNotBlank() }?.let(::add)
            addressLine2?.takeIf { it.isNotBlank() }?.let(::add)
            city.orEmpty().takeIf { it.isNotBlank() }?.let(::add)
            district.orEmpty().takeIf { it.isNotBlank() }?.let(::add)
            state.orEmpty().takeIf { it.isNotBlank() }?.let(::add)
            country.orEmpty().takeIf { it.isNotBlank() }?.let(::add)
            pinCode.orEmpty().takeIf { it.isNotBlank() }?.let(::add)
        }.joinToString(", ")

        return SchoolRowUi(
            id = id.orEmpty(),
            name = name.orEmpty(),
            code = code.orEmpty(),
            city = city.orEmpty(),
            contactName = primaryContactName.orEmpty(),
            students = studentCount,
            isActive = isActive == true,
            board = board.orEmpty(),
            schoolType = schoolType.orEmpty(),
            medium = medium.orEmpty(),
            location = location,
            createdOn = formatApiDate(createdAt.orEmpty()),
            updatedOn = formatApiDate(updatedAt.orEmpty()),
            createdBy = createdById.orEmpty(),
            updatedBy = updatedById.orEmpty(),
            addressLine1 = addressLine1.orEmpty(),
            addressLine2 = addressLine2.orEmpty(),
            district = district.orEmpty(),
            state = state.orEmpty(),
            country = country.orEmpty(),
            pinCode = pinCode.orEmpty(),
            schoolPhone = schoolPhone.orEmpty(),
            schoolEmail = schoolEmail.orEmpty(),
            primaryPhone = primaryContactPhone.orEmpty(),
            primaryEmail = primaryContactEmail.orEmpty(),
            secondaryContactName = secondaryContactName.orEmpty(),
            secondaryPhone = secondaryContactPhone.orEmpty(),
            secondaryEmail = secondaryContactEmail.orEmpty(),
            attachmentFileId = attachmentFileIds?.firstOrNull().orEmpty(),
            schoolLogoUrl = schoolLogoUrl.orEmpty(),
            schoolDescription = schoolDescription.orEmpty()
        )
    }

    private fun formatApiDate(value: String): String {
        val normalized = value.trim()
        if (normalized.isBlank()) return ""
        val datePart = normalized.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return normalized
        val parts = datePart.split("-")
        if (parts.size != 3) return normalized
        val year = parts[0]
        val month = parts[1]
        val day = parts[2]
        return if (year.length == 4 && month.length == 2 && day.length == 2) {
            "$day-$month-$year"
        } else {
            normalized
        }
    }

    fun clear() {
        loadJob?.cancel()
        analyticsJob?.cancel()
        searchDebouncer.cancel()
        scope.cancel()
    }

}

private fun SchoolEditorDraft.toSchoolCreateRequest(): SchoolCreateRequest {
    return SchoolCreateRequest(
        name = name.trim(),
        board = SystemLookupOptions.serverCode("SCHOOL_BOARD", board),
        schoolType = SystemLookupOptions.serverCode("SCHOOL_TYPE", type),
        medium = SystemLookupOptions.serverCode("SCHOOL_MEDIUM", medium),
        addressLine1 = addressLine1.trim(),
        addressLine2 = addressLine2.trim().ifBlank { null },
        city = city.trim(),
        district = district.trim(),
        state = state.trim(),
        country = country.trim(),
        pinCode = pinCode.trim(),
        schoolPhone = schoolPhone.trim(),
        schoolEmail = schoolEmail.trim(),
        primaryContactName = contactName.trim(),
        primaryContactPhone = primaryPhone.trim(),
        primaryContactEmail = primaryEmail.trim(),
        secondaryContactName = secondaryContactName.trim().ifBlank { null },
        secondaryContactPhone = secondaryPhone.trim().ifBlank { null },
        secondaryContactEmail = secondaryEmail.trim().ifBlank { null },
        schoolDescription = schoolDescription.trim().ifBlank { null },
    )
}

private fun SchoolCreateRequest.validateCreateSchool(): String? {
    return when {
        name.isBlank() -> "School name is required."
        board.isBlank() -> "Board is required."
        schoolType.isBlank() -> "School type is required."
        medium.isBlank() -> "Medium is required."
        addressLine1.isBlank() -> "Address line 1 is required."
        city.isBlank() -> "City is required."
        district.isBlank() -> "District is required."
        state.isBlank() -> "State is required."
        country.isBlank() -> "Country is required."
        pinCode.isBlank() -> "Pin code is required."
        schoolPhone.isBlank() -> "School phone is required."
        schoolEmail.isBlank() -> "School email is required."
        primaryContactName.isBlank() -> "Primary contact name is required."
        primaryContactPhone.isBlank() -> "Primary contact phone is required."
        primaryContactEmail.isBlank() -> "Primary contact email is required."
        else -> null
    }
}
